import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import Layout from 'components/Layout';
import Meta from 'components/Meta';
import { TrainingDetailContainer } from 'styles/training-detail';
import {
  TrainingDetailHero,
  TwoColumnRedAndBlackTile,
  SummaryAndBio,
  PricingDetails,
  FAQSection,
  CourseOverview,
} from 'components/TrainingDetailSections';
import { AWS_PROFESSIONAL, AWS_ASSOCIATE, KUBE_DEV, KUBE_ADMIN } from 'components/TrainingDetailSections/CourseOverviewConstant';
import SectionContact from 'components/SectionContact';
import escapeToHTML from 'utils/escapeToHTML';

import WorkbookIcon from 'images/icons/workbooks.svg';
import ChatIcon from 'images/icons/chat.svg';
import BlogIcon from 'images/icons/blogs.svg';
import ShieldIcon from 'images/icons/shield.svg';

const AtcTraining = ({ training, path }) => {
  const isCloudTraining = training.trainingId.startsWith('AC')
  const twoColumRedAndBlackTileData = [
    {
      title: 'Who benefits?',
      description: escapeToHTML(training.whoWillBenefit),
    },
    {
      title: 'Learning Outcomes',
      description: escapeToHTML(training.learningGoals),
    },
  ];

  const benefits = {
    title: 'ATC Training - Exclusive Benefits',
    list: [
      <>
        Training Conducted by <b>Seasoned Industry Experts</b>
      </>,
      <>
        <b>1 Year Membership</b> Covering Live Job Support
      </>,
      <>
        <b>Monthly Q&A Sessions</b> with Industry Experts
      </>,
      <>
        <b>$200</b> Discount for Your Next Training
      </>,
      <>
        <b>$50 Visa Gift Card</b> for Every Successful Referral
      </>,
    ],
  };

  const whatYouGetDetails = [
    {
      type: 'svg',
      icon: WorkbookIcon,
      title: (
        <>
          Workbooks <br />& Course <br />
          Materials
        </>
      ),
    },
    {
      type: 'svg',
      icon: BlogIcon,
      title: (
        <>
          Exclusive <br />
          Blogs & <br />
          Whitepapers
        </>
      ),
    },
    {
      type: 'svg',
      icon: ChatIcon,
      title: (
        <>
          Private Slack <br />
          Group to <br />
          Collaborate
        </>
      ),
    },
    {
      type: 'svg',
      icon: ShieldIcon,
      title: (
        <>
          Course <br />
          Completion <br />
          Certificate
        </>
      ),
    },
  ];

  const faqList = [
    {
      question: 'Why choose ATC for Product Management Training?',
      answer: (
        <>
          <p>
            Well, firstly,{' '}
            <b>ATC is much more than the regular training provider.</b>
            We align with multi-national organizations as a{' '}
            <b>strategic partner and create scalable software products</b> that
            add value to our customers. We help them{' '}
            <b>identify emerging trends and understand market requirements</b>.
            Training is one of the many offerings we provide, where we translate
            our global experience, insights, innovations, and expertise to help
            you gain a competitive advantage as a Product Management (PM)
            professional.
          </p>
        </>
      ),
    },
    {
      question: 'What are the benefits of ATC’s Product Management Training?',
      answer: (
        <>
          <p>
            We provide a highly collaborative training program that is pragmatic
            and aligns with real-time scenarios. In just 4 weeks, you will be
            able to:
            <ul>
              <li>
                <b>Exhibit mastery</b> of core PM concepts and skills
              </li>
              <li>
                <b>Develop the product mindset</b> to excel in your current role
              </li>
              <li>
                <b>Boost credibility</b> within your team
              </li>
              <li>
                <b>Stand out from the crowd</b>
              </li>
              <li>
                <b>Propel your career</b> more rapidly
              </li>
            </ul>
          </p>
        </>
      ),
    },
    {
      question: 'What happens after the training?',
      answer: (
        <>
          <p>
            Upon completion of the 4-week training module, you earn a{' '}
            <b>Course Completion Certificate</b>.
            <br /> But training is not an END; it’s a new BEGINNING. You’ve
            formally acquired the skills to navigate the world of Product
            Management. To facilitate your journey, we keep the network live
            through the following channels by providing exclusive access to the
            PM training community:{' '}
            <ul>
              <li>Slack</li> <li>LinkedIn</li>
            </ul>{' '}
            Here, you can continue to interact with your batch mates and
            trainers.
            <br />
            <br />
            In addition to building a community of Product Management
            professionals, we also conduct the following activities.
            <ol>
              <li>
                1. <b>Monthly Q&A sessions</b> with Industry Experts, Featuring
                PM Topics in Trend.
              </li>{' '}
              <li>
                2. <b>1 Year Membership</b> Covering Live Job Support
              </li>
            </ol>
          </p>
        </>
      ),
    },
    {
      question: 'What materials do I get?',
      answer: (
        <p>
          When you enroll, you get downloadable workbooks and course materials.
          Also, you will be notified when we publish blogs and white papers on
          Product Management.
        </p>
      ),
    },
    {
      question: 'Who are the trainers?',
      answer: (
        <p>
          Our trainers are highly successful Product Managers at ATC and several
          top tech companies, with an average industry experience of over a
          decade. With extensive experience in measuring Markets & Customer
          Goals, strategizing Opportunities & Priorities, planning Execution &
          Delivery, and Marketing & Selling outcomes, they are the cream of the
          crop in the industry.
        </p>
      ),
    },
    {
      question: 'Do I need to have a technology background?',
      answer: (
        <p>
          <b>Absolutely NOT</b>. Technical skills are good to have, but it does
          not mandate success in Product Management. A Product Manager is
          involved in every phase of the product lifecycle that encompasses
          ideation, design, engineering, and marketing. The curriculum will help
          you uncover the art of building great products from a PM
          professional’s standpoint and successfully interact with the various
          teams involved in the process.
        </p>
      ),
    },
    {
      question: 'When does the training happen?',
      answer: (
        <p>
          The training spans 4 weeks. Training classes happen on Tuesdays and
          Thursdays from 9.30 am to 11.30 am Central. The breaks in between
          weekly sessions are intended to help you practice and refresh your
          knowledge before taking up new modules. The Slack group will be open
          for you to interact with your class and trainer.
        </p>
      ),
    },
    {
      question: 'What is the mode of training?',
      answer: (
        <p>
          In response to concerns around COVID-19 and to keep our trainees safe,
          we are currently delivering virtual instructor-led online training.
          In-person training will also be available in all major cities in the
          US when the situation gets back to normal.
        </p>
      ),
    },
    {
      question: 'How many people do you train in a class?',
      answer: (
        <p>
          The maximum number would be 20 per class. Being a Lean-Agile
          organization, we keep our class size as lean as possible to ensure
          agility, interactivity, and collaboration.
        </p>
      ),
    },
    {
      question: 'What do I get when I refer people to the training?',
      answer: (
        <p>
          We welcome you to help us grow the Product Management community. And
          for every referral who enrolls in the training, we give you a{' '}
          <b>$100 Visa Gift Card</b>. Best of all, there’s no limit to the
          number of people you can refer.
          <br />
          <br />
          Simply{' '}
          <a href="https://american-technology.net/contact-us/" target="_blank">
            contact us
          </a>{' '}
          with the <b>name, email ID, and phone number</b> of the person or
          people whom you are referring to our training program.
        </p>
      ),
    },
    {
      question: 'Do you conduct training for private batches?',
      answer: (
        <p>
          Yes, we do conduct private training for groups at the organization’s
          preferred location or online. Here are a few benefits when taking up a
          private training class:{' '}
          <ul>
            <li>
              Individual attention to help with organization-specific objectives
              concerning Product Development.
            </li>
            <li>
              Premium Support to coach and guide team members or a group during
              the Product Development lifecycle.
            </li>
          </ul>{' '}
          Please{' '}
          <a href="https://american-technology.net/contact-us/" target="_blank">
            contact us
          </a>{' '}
          with your inquiry, and we will get back to you at the earliest.
        </p>
      ),
    },
    {
      question: 'What is the transfer and cancellation policy?',
      answer: (
        <>
          <p>
            In order to provide the most experienced trainers and a superior
            classroom environment, we must ask you to adhere to our cancellation
            policy. If it is necessary to cancel or reschedule, please notify us
            immediately. Our cancellation policy is as follows:
          </p>
          <ul>
            <li>
              A full refund can be claimed 30 days prior to the class start
              date.
            </li>
            <li>
              No refund will be allowed within 30 days from the class start
              date. However, students may reschedule for another class within
              one year of the cancellation date.
            </li>
            <li>
              No reschedules allowed within 7 business days unless it’s an
              unforeseen circumstance.
            </li>
            <li>
              In the event the training is canceled due to low enrollment or
              unforeseen natural events, ATC will issue a 100% refund.
            </li>
          </ul>
          <p>
            Do get in touch with your Training Specialist for any requests.
            <br />
            <br />
            <b>
              Product Management is one of the most sought-after and highly-paid
              professions
            </b>{' '}
            today and the foreseeable future. That said, the cost of training is
            only bound to increase. Hence, we recommend that instead of
            canceling, you may opt to transfer to a convenient schedule.
          </p>
        </>
      ),
    },
  ];

  const cloudFaqList = [{
    question: 'Why choose ATC for Cloud Computing training?',
    answer: (
      <>
        <p>
          Well, firstly,{' '}
          <b>ATC is much more than the regular training provider.</b>{' '}
          We align with multi-national organizations as a{' '}
          <b>strategic partner and create scalable software products and solutions</b>{' '}
          that add value to our customers. We help them{' '}
          <b>identify emerging trends and understand market requirements</b>.
          Training is one of the many offerings we provide, where we translate
          our global experience, insights, innovations, and expertise to help
          you gain a competitive advantage as a Cloud Computing
          professional.
        </p>
      </>
    ),
  },
  {
    question: 'What are the benefits of ATC’s Cloud Computing Training?',
    answer: (
      <>
        <p>
          We provide a highly collaborative training program that is pragmatic
          and aligns with real-time scenarios. In just 2 weeks, you will be
          able to:
          <ul>
            <li>
              <b>Exhibit mastery</b> of core Cloud Computing concepts and skills
            </li>
            <li>
              <b>Develop the Cloud technology mindset</b> to excel in the dynamic workplace
            </li>
            <li>
              <b>Boost credibility</b> within your team
            </li>
            <li>
              <b>Stand out from the crowd</b>
            </li>
            <li>
              <b>Propel your career</b> more rapidly
            </li>
          </ul>
        </p>
      </>
    ),
  },
  {
    question: 'What happens after the training?',
    answer: (
      <>
        <p>
          The 2-week training program will get you prepared to take up the certification exam.
          <br /> Training is not an END; it’s a new BEGINNING. You’ve formally acquired the skills to navigate the world of Cloud Computing. To facilitate your journey, we keep the network live through the following channels by providing exclusive access to the Cloud training community:{' '}
          <ul>
            <li>Slack</li> <li>LinkedIn</li>
          </ul>{' '}
          Here, you can continue to interact with your batch mates and
          trainers.
          <br />
          <br />
          In addition to building a community of Cloud Computing
          professionals, we also conduct the following activities.
          <ol>
            <li>
              1. <b>Monthly Q&A sessions</b> with Industry Experts, Featuring
              Cloud Computing topics in trend.
            </li>{' '}
            <li>
              2. <b>Post-training support</b> as and when required to help you clear the certification
            </li>
          </ol>
        </p>
      </>
    ),
  },
  {
    question: 'What materials do I get?',
    answer: (
      <p>
        When you enroll, you get downloadable workbooks and course materials.
        Also, you will be notified when we publish blogs and white papers on
        Cloud Computing.
      </p>
    ),
  },
  {
    question: 'Who are the trainers?',
    answer: (
      <p>
        The trainers are seasoned industry professionals and leading Cloud practitioners who bring current best practices
        and trends to the training sessions that relate to actual workplace scenarios.
      </p>
    ),
  },
  {
    question: 'What is the mode of training?',
    answer: (
      <p>
        In response to concerns around COVID-19 and to keep our trainees safe,
        we are currently delivering virtual instructor-led online training.
        In-person training will also be available in all major cities in the
        US when the situation gets back to normal.
      </p>
    ),
  },
  {
    question: 'What do I get when I refer people to the training?',
    answer: (
      <p>
        We welcome you to help us grow the Cloud Computing community. And
        for every referral who enrolls in the training, we give you a{' '}
        <b>$50 Visa Gift Card</b>. Best of all, there’s no limit to the
        number of people you can refer.
        <br />
        <br />
        Simply{' '}
        <a href="https://american-technology.net/contact-us/" target="_blank">
          contact us
        </a>{' '}
        with the <b>name, email ID, and phone number</b> of the person or
        people whom you are referring to our training program.
      </p>
    ),
  },
  {
    question: 'What is the transfer and cancellation policy?',
    answer: (
      <>
        <p>
          In order to provide the most experienced trainers and a superior
          classroom environment, we must ask you to adhere to our cancellation
          policy. If it is necessary to cancel or reschedule, please notify us
          immediately. Our cancellation policy is as follows:
        </p>
        <ul>
          <li>
            A full refund can be claimed 15 days prior to the class start
            date.
          </li>
          <li>
            No refund will be allowed within 14 days from the class start
            date. However, students may reschedule for another class within
            one year of the cancellation date.
          </li>
          <li>
            No reschedules allowed within 7 business days unless it’s an
            unforeseen circumstance.
          </li>
          <li>
            In the event the training is canceled due to low enrollment or
            unforeseen natural events, ATC will issue a 100% refund.
          </li>
        </ul>
        <p>
          Do get in touch with your Training Specialist for any requests.
          <br />
          <br />
          <b>
            Certified Cloud professionals is one of the most sought-after and highly-paid
          </b>{' '}
          today and the foreseeable future. That said, the cost of training is
          only bound to increase. Hence, we recommend that instead of
          canceling, you may opt to transfer to a convenient schedule.
        </p>
      </>
    ),
  }];

  const courseOverview = {
    modules: [
      [
        {
          title:
            'Introduction to Product Management, Processes, Product  Lifecycle',
          topics: [
            'Overview of Product Manager’s role',
            'Differences between Product vs. Project Managers',
            'Types of PMs (Business PM, Design PM, Tech PM, Data PM)',
            'Product Management process',
            'Product Phases/lifecycle (Alpha, BETA, RC, GA, Sunset)',
          ],
        },
        {
          title: 'Understanding Market, Competition, Strategy',
          topics: [
            'Target Market',
            'Competitive Intelligence',
            'Target Personas ',
            'Pricing',
          ],
        },
      ],
      [
        {
          title: 'Understanding Product-Market fit and building an MVP',
          topics: [
            'New Product/Feature Hypothesis',
            'Product Strategy to measure Opportunity',
            'Target User / Segmentation',
            'Customer Development & Research Methodology',
            'Achieving Product-Market Fit  + Case Study',
            'Build, Measure, Learn your MVP',
          ],
        },
        {
          title: 'Roadmapping, Prioritization Models, JTBD Framework',
          topics: [
            'Gather product items/ customer feedback',
            'Prioritization Models',
            'Personas & Jobs-to-be-done (JTBD) framework',
          ],
        },
      ],
      [
        {
          title:
            'Writing effective User stories and Visual Diagramming Techniques',
          topics: [
            'Types of features - Functional, Non-functional, Integration',
            'Writing Effective User Stories',
            'Visual Diagramming Techniques',
          ],
        },
        {
          title: 'UI, UX, Product Design and Design Systems for PMs',
          topics: [
            'UX for Product Managers',
            'UI vs. UX',
            'Product Design Principles for PMs',
            'Importance of UX Copy',
            'Intro to Design System',
          ],
        },
        {
          title: 'Fundamentals of APIs for PMs',
          topics: [
            'Fundamentals of Data models & APIs',
            'Dev Estimation',
            'Sprint Planning',
            'Test Strategy/techniques',
          ],
        },
      ],
      [
        {
          title: 'Product Launch & Go-to-Market Strategies',
          topics: [
            'Intro to Product Marketing',
            'Brand and Positioning',
            'Messaging',
            'Pricing Strategy',
            'Marketing Collateral',
            'Product Training & Sales Enablement',
          ],
        },
        {
          title: 'Product Metrics & Analytics',
          topics: [
            'SaaS Metrics',
            'Choosing your North Star',
            'Product/Usage Metrics',
            'AARRR - Pirate Metrics',
            'Customer Loyalty Metrics (NPS, CES, CSAT)',
          ],
        },
      ],
    ],
    // bonus: [
    //   {
    //     title: 'Bonus',
    //     topics: ['Books to read', 'Blogs to subscribe', 'PM experts to follow'],
    //   },
    // ],
  };

  const getCourseOverviewData = () => {
    if (isCloudTraining) {
      if (training.trainingId.startsWith('ACAA')) {
        return AWS_ASSOCIATE;
      } else if (training.trainingId.startsWith('ACAB')) {
        return AWS_PROFESSIONAL;
      } else if (training.trainingId.startsWith('ACKA')) {
        return KUBE_DEV;
      } else if (training.trainingId.startsWith('ACKB')) {
        return KUBE_ADMIN;
      }
    }
    return courseOverview;
  }

  return (
    <>
      <Meta
        title={`${training.certificationName} | ATC Training` || 'ATC Training'}
      />
      <Layout lightTheme path={path}>
        <TrainingDetailHero training={training}></TrainingDetailHero>
        <TrainingDetailContainer>
          <section>
            <div className="training-schedule-main">
              <div className="container">
                <SummaryAndBio
                  training={training}
                  showBio={true}
                ></SummaryAndBio>
              </div>
            </div>
          </section>
          <TwoColumnRedAndBlackTile
            columnData={twoColumRedAndBlackTileData}
          ></TwoColumnRedAndBlackTile>
          <CourseOverview overviewData={getCourseOverviewData()} isCloudTraining={isCloudTraining} />
          <PricingDetails
            training={training}
            benefits={benefits}
            whatYouGetDetails={whatYouGetDetails}
            isStrikeThrough
          ></PricingDetails>
          <FAQSection faqList={isCloudTraining ? cloudFaqList : faqList}></FAQSection>
        </TrainingDetailContainer>
        <SectionContact
          title="Lets talk about your project"
          cta="Contact Us"
          to="/contact-us/"
        />
      </Layout>
    </>
  );
};

AtcTraining.propTypes = {
  training: PropTypes.object,
  path: PropTypes.string,
};

export default AtcTraining;
